/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { FocusScope } from 'react-aria';
import cx from 'classnames';

import styles from './SearchResultsMapV2Modal.module.scss';

interface SearchResultsMapV2ModalProps {
    isOpen: boolean;
    onClose: () => void;
    triggerRef?: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
}

export function SearchResultsMapV2Modal({
    isOpen,
    onClose,
    triggerRef,
    children,
}: SearchResultsMapV2ModalProps) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const wasOpenRef = React.useRef(false);

    React.useEffect(() => {
        if (!isOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, onClose]);

    React.useEffect(() => {
        if (isOpen) {
            wasOpenRef.current = true;
            containerRef.current?.focus();
        } else if (wasOpenRef.current) {
            // On close, return focus to the trigger (e.g. the "Show Map" button)
            // so keyboard users can press Enter again to reopen.
            wasOpenRef.current = false;
            triggerRef?.current?.focus();
        }
    }, [isOpen, triggerRef]);

    React.useEffect(() => {
        if (!isOpen) return;
        // Lock <html> scroll while the map modal is open so background scroll
        // can't bleed through under the fixed-position map on iOS Safari.
        const { documentElement } = document;
        const previous = documentElement.style.overflow;
        documentElement.style.overflow = 'hidden';
        return () => {
            documentElement.style.overflow = previous;
        };
    }, [isOpen]);

    return (
        <div
            ref={containerRef}
            tabIndex={-1}
            className={cx(styles.modal, { [styles.isOpen]: isOpen })}
            role="dialog"
            aria-modal={isOpen}
            aria-hidden={!isOpen}
            aria-label="Map view"
        >
            <FocusScope contain={isOpen}>{children}</FocusScope>
        </div>
    );
}
